// Reporting time calculator: pure functions shared by the page and the tests.

export const DEFAULT_INPUTS = Object.freeze({
  reports: 6,
  hoursPerRun: 1.5,
  runsPerMonth: 4,
  fixHoursPerMonth: 8,
  hourlyCost: 75,
  automationShare: 70,
  buildCost: 12000,
  monthlyUpkeep: 400,
});

export const LIMITS = Object.freeze({
  reports: [0, 500],
  hoursPerRun: [0, 200],
  runsPerMonth: [0, 31 * 24],
  fixHoursPerMonth: [0, 2000],
  hourlyCost: [0, 2000],
  automationShare: [0, 100],
  buildCost: [0, 10_000_000],
  monthlyUpkeep: [0, 1_000_000],
});

/** Turn any user value (string, blank, NaN, negative, huge) into a safe number. */
export function sanitize(key, value) {
  const [min, max] = LIMITS[key];
  const number = typeof value === 'number' ? value : Number(String(value ?? '').replace(/[$,%\s]/g, ''));
  if (!Number.isFinite(number)) return min;
  return Math.min(max, Math.max(min, number));
}

export function sanitizeAll(raw) {
  const clean = {};
  for (const key of Object.keys(DEFAULT_INPUTS)) clean[key] = sanitize(key, raw?.[key]);
  return clean;
}

export function calculate(raw) {
  const input = sanitizeAll(raw);
  const prepHours = input.reports * input.hoursPerRun * input.runsPerMonth;
  const manualHours = prepHours + input.fixHoursPerMonth;
  const manualCost = manualHours * input.hourlyCost;
  const hoursSaved = manualHours * (input.automationShare / 100);
  const grossSavings = hoursSaved * input.hourlyCost;
  const netMonthly = grossSavings - input.monthlyUpkeep;
  const paybackMonths = netMonthly > 0 ? input.buildCost / netMonthly : null;
  const firstYearNet = netMonthly * 12 - input.buildCost;
  return {
    input,
    prepHours,
    manualHours,
    manualCost,
    hoursSaved,
    grossSavings,
    netMonthly,
    paybackMonths,
    firstYearNet,
    pays: paybackMonths !== null,
  };
}

export function formatMoney(value) {
  const rounded = Math.round(value);
  const sign = rounded < 0 ? '-' : '';
  return `${sign}$${Math.abs(rounded).toLocaleString('en-US')}`;
}

export function formatHours(value) {
  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded.toLocaleString('en-US')} ${rounded === 1 ? 'hour' : 'hours'}`;
}

export function formatPayback(months) {
  if (months === null) return 'Does not pay back';
  if (months < 1) return 'Under 1 month';
  if (months > 120) return 'More than 10 years';
  const rounded = Math.round(months * 10) / 10;
  return `${rounded} ${rounded === 1 ? 'month' : 'months'}`;
}

/** Read inputs from a URL query string; unknown or invalid values fall back to defaults. */
export function inputsFromSearch(search) {
  const params = new URLSearchParams(search || '');
  const result = { ...DEFAULT_INPUTS };
  for (const key of Object.keys(DEFAULT_INPUTS)) {
    const value = params.get(key);
    if (value !== null && value.trim() !== '' && Number.isFinite(Number(value))) result[key] = sanitize(key, value);
  }
  return result;
}

export function searchFromInputs(inputs) {
  const params = new URLSearchParams();
  const clean = sanitizeAll(inputs);
  for (const key of Object.keys(DEFAULT_INPUTS)) params.set(key, String(clean[key]));
  return `?${params.toString()}`;
}
